import { useState, useEffect, useMemo, useCallback } from "react";
import {
  initialFilters,
  isLincolnClientHandlingUser,
} from "../../orders/filterConfigs";
import { isCanadianPreorderUser } from "../../utils/canadianPreorderExperience";
import * as OrdersPageAnalytics from "../services/analytics/OrdersPageAnalytics";

export const useOrderFilters = (onFiltersApplied, user) => {
  const isCanadianPreorder = isCanadianPreorderUser(user);
  const isUpdatedLincolnUser = isLincolnClientHandlingUser(user);
  const identity = JSON.stringify([
    user?.market,
    user?.brand,
    user?.dealerId,
    user?.salesCode,
    isCanadianPreorder,
    isUpdatedLincolnUser,
  ]);
  const defaults = useMemo(
    () =>
      user?.brand === "L"
        ? isUpdatedLincolnUser
          ? { orderStatus: [] }
          : { actionRequired: [], orderStatus: [] }
        : initialFilters,
    [user?.brand, isUpdatedLincolnUser]
  );
  const [filterState, setFilterState] = useState({
    identity,
    filters: defaults,
  });
  // Expose reset values during the identity-change render, before queries run.
  const selectedFilters =
    filterState.identity === identity ? filterState.filters : defaults;

  useEffect(() => {
    setFilterState((current) =>
      current.identity === identity ? current : { identity, filters: defaults }
    );
  }, [identity, defaults]);

  useEffect(() => {
    if (onFiltersApplied) {
      onFiltersApplied(selectedFilters);
    }
  }, [selectedFilters, onFiltersApplied]);

  const handleActionRequiredFilter = (value, checked) => {
    return checked ? [value] : [];
  };

  const handleAddFilter = (currentFilters, value, category) => {
    if (currentFilters.includes(value)) {
      return currentFilters;
    }
    OrdersPageAnalytics.trackFilterApplied(category, value, user);
    return [...currentFilters, value];
  };

  const handleRemoveFilter = (currentFilters, value) => {
    return currentFilters.filter((item) => item !== value);
  };

  const handleStandardFilter = (currentFilters, value, checked, category) => {
    return checked
      ? handleAddFilter(currentFilters, value, category)
      : handleRemoveFilter(currentFilters, value);
  };

  const handleFiltersApplied = useCallback(
    (filters) => {
      setFilterState({
        identity,
        filters: isCanadianPreorder
          ? {
              ...initialFilters,
              actionRequired: filters.actionRequired || [],
              orderStatus: filters.orderStatus || [],
            }
          : filters,
      });
    },
    [identity, isCanadianPreorder]
  );

  const handleFilterChange = (category, value, checked) => {
    if (
      isCanadianPreorder &&
      !["actionRequired", "orderStatus"].includes(category)
    )
      return;

    setFilterState((current) => {
      const prev = current.identity === identity ? current.filters : defaults;
      const currentCategoryFilters = prev[category] || [];

      const newCategoryFilters =
        category === "actionRequired"
          ? handleActionRequiredFilter(value, checked)
          : handleStandardFilter(
              currentCategoryFilters,
              value,
              checked,
              category
            );

      return {
        identity,
        filters: { ...prev, [category]: newCategoryFilters },
      };
    });
  };

  const handleClearFilters = () => {
    setFilterState({ identity, filters: defaults });
    OrdersPageAnalytics.trackClearFilters(user);
  };

  return {
    selectedFilters,
    handleFiltersApplied,
    handleFilterChange,
    handleClearFilters,
  };
};
